import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  Image,
  Heading,
  Divider,
  HStack,
  Select,
  Icon,
  Grid,
  Spacer,
} from "@chakra-ui/react";
import CloudIcon from "../../assets/images/cloud-footprint.png";
import Translate from "../LanguageTranslation/Translate";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import OffsetTripDetails from "./OffsetTripDetails";
import axios from "axios";
import { EditIcon, CheckIcon, CloseIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaPlaneDeparture, FaUser, FaMoon } from "react-icons/fa";

/* palette (shared with the rest of the tool) */
const GOLD = "#ffaa25";

const OffsetCalculator = ({ cause, setCause, onMoveToStep3 }) => {
  // tab + flight detail state
  const [tripTab, setTripTab] = useState("flight");
  const [tripMode, setTripMode] = useState("one");
  const [tripType, setTripType] = useState("round");
  const [cabinTab, setCabinTab] = useState("economy");
  const [passengers, setPassengers] = useState(1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [counts, setCounts] = useState({ short: 0, medium: 0, long: 0 });

  // Accommodation tab state (in OffsetCalculator.jsx)
  const [accomCountry, setAccomCountry] = useState("");
  const [accomType, setAccomType] = useState("hotel");
  const [accomRooms, setAccomRooms] = useState("");
  const [accomNights, setAccomNights] = useState("");
  const [accomResult, setAccomResult] = useState(null);

  // Car tab state
  const [carVehicleType, setCarVehicleType] = useState("standard");
  const [carDistance, setCarDistance] = useState("");
  const [carFuelType, setCarFuelType] = useState("gasoline");
  const [carFuelConsumed, setCarFuelConsumed] = useState("");
  const [carResult, setCarResult] = useState(null);

  // Boat tab state
  const [boatType, setBoatType] = useState("cruise");
  const [boatDays, setBoatDays] = useState(1);
  const [boatPeople, setBoatPeople] = useState(1);
  const [boatResult, setBoatResult] = useState(null);

  // Train tab state
  const [trainTripMode, setTrainTripMode] = useState("one");
  const [trainTripType, setTrainTripType] = useState("round");
  const [trainPassengers, setTrainPassengers] = useState(1);
  const [trainClass, setTrainClass] = useState("economy");
  const [trainFrom, setTrainFrom] = useState("");
  const [trainTo, setTrainTo] = useState("");
  const [trainCounts, setTrainCounts] = useState({
    short: 0,
    medium: 0,
    long: 0,
  });
  const [trainResult, setTrainResult] = useState(null);

  // holds the JSON from POST /co2-emissions/calculate/flight
  const [result, setResult] = useState(null);

  // New state for cause input
  // const [cause, setCause] = useState("");
  // New state for loading and error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // New state for editable cost
  const [customCost, setCustomCost] = useState("");

  // Add state for trip selection
  const [selectedTrip, setSelectedTrip] = useState("");
  const [tripName, setTripName] = useState("");

  // Add state for editing cost
  const [isEditingCost, setIsEditingCost] = useState(false);
  const [tempCost, setTempCost] = useState("");

  // Add state for excluded categories
  const [excludedCategories, setExcludedCategories] = useState(new Set());

  // Currency context (call hook unconditionally)
  const { currency, convertCurrency } = useCurrency();

  // Load cause from localStorage on component mount only if cause is empty
  useEffect(() => {
    const savedCause = localStorage.getItem("selectedCause");
    if (savedCause && (!cause || cause.trim() === "")) {
      setCause(savedCause);
    }
  }, []); // Only run on mount, not when cause changes

  // Save cause to localStorage whenever it changes
  useEffect(() => {
    console.log("Cause changed to:", cause);
    if (cause && cause.trim() !== "") {
      localStorage.setItem("selectedCause", cause);
      console.log("Saved to localStorage:", cause);
    }
  }, [cause]);

  // Function to toggle category exclusion
  const toggleCategoryExclusion = (category) => {
    setExcludedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Function to clear all results when switching tabs
  const clearResults = () => {
    setResult(null);
    setAccomResult(null);
    setCarResult(null);
    setBoatResult(null);
    setTrainResult(null);
    setCustomCost("");
    // setCause("");
    setError(null);
    setExcludedCategories(new Set());
  };

  // Wrapper function for setTripTab (persist results between tabs)
  const handleTripTabChange = (newTab) => {
    setTripTab(newTab);
  };

  // Cumulative emissions across all categories (kg CO2e), excluding deleted categories
  const totalEmissionsKg =
    (excludedCategories.has("flight") ? 0 : result?.emissions_kg_co2e || 0) +
    (excludedCategories.has("accommodation")
      ? 0
      : accomResult?.emissions_kg_co2e || 0) +
    (excludedCategories.has("car") ? 0 : carResult?.emissions_kg_co2e || 0) +
    (excludedCategories.has("boat") ? 0 : boatResult?.emissions_kg_co2e || 0) +
    (excludedCategories.has("train") ? 0 : trainResult?.emissions_kg_co2e || 0);

  // Individual category breakdown (kg CO2e)
  const emissionBreakdown = {
    flight: result?.emissions_kg_co2e || 0,
    accommodation: accomResult?.emissions_kg_co2e || 0,
    car: carResult?.emissions_kg_co2e || 0,
    boat: boatResult?.emissions_kg_co2e || 0,
    train: trainResult?.emissions_kg_co2e || 0,
  };

  // Per-category amount in selected currency
  const categoryAmounts = {
    flight:
      parseFloat(
        convertCurrency((emissionBreakdown.flight || 0) * 0.2, "USD")
      ) || 0,
    accommodation:
      parseFloat(
        convertCurrency((emissionBreakdown.accommodation || 0) * 0.2, "USD")
      ) || 0,
    car:
      parseFloat(convertCurrency((emissionBreakdown.car || 0) * 0.2, "USD")) ||
      0,
    boat:
      parseFloat(convertCurrency((emissionBreakdown.boat || 0) * 0.2, "USD")) ||
      0,
    train:
      parseFloat(
        convertCurrency((emissionBreakdown.train || 0) * 0.2, "USD")
      ) || 0,
  };

  // Detect if current flight result is from multiple-trips endpoint
  const isFlightMulti = !!(
    result && result.total_emissions_kg_co2e !== undefined
  );
  // Detect if current train result is from multiple-trips endpoint
  const isTrainMulti = !!(
    trainResult && trainResult.total_emissions_kg_co2e !== undefined
  );

  const capitalize = (val) =>
    val && typeof val === "string"
      ? val.charAt(0).toUpperCase() + val.slice(1)
      : val;

  // Calculate cost in USD
  const costUSD = totalEmissionsKg ? totalEmissionsKg * 0.2 : 0;
  // Convert to selected currency
  const convertedCost = parseFloat(convertCurrency(costUSD, "USD")) || 0;

  // Use custom cost if set, otherwise use calculated
  const displayCost = customCost !== "" ? customCost : convertedCost;

  // Fetch email from localStorage
  const getEmail = () => {
    try {
      return localStorage.getItem("email") || "";
    } catch {
      return "";
    }
  };

  // Handle Offset Now click
  const handleOffsetNow = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        amount: parseFloat(displayCost),
        currency: currency || "USD",
        cause: cause,
        email: getEmail(),
        trip_id: selectedTrip || "test-trip-id",
      };
      const response = await axios.post("/contributions/donate", payload);
      setLoading(false);
      // console.log("response", response);
      // Check if payment URL is provided in response
      if (response.data && response.data.payment_url) {
        // Move to Step 3 with payment data instead of opening directly
        onMoveToStep3({
          paymentUrl: response.data.payment_url,
          amount: displayCost,
          currency: currency || "USD",
          cause: cause,
          emissions: totalEmissionsKg,
          tripType: "all",
        });
      } else {
        alert("Thank you for your contribution!");
      }
    } catch (err) {
      setLoading(false);
      setError("Failed to process donation. Please try again.");
    }
  };

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const trainSwap = () => {
    setTrainFrom(trainTo);
    setTrainTo(trainFrom);
  };

  return (
    <Flex
      maxW={{ base: "90%", md: "90%", lg: "80%", xl: "80%" }}
      mx="auto"
      pt={12}
      pb={20}
      px={{ base: 4, md: 6 }}
      gap={8}
      direction={{ base: "column", md: "row" }}
    >
      {/* ─── Left: Trip Details ──────────────────────────────── */}
      <Box flex="2">
        <OffsetTripDetails
          // FLights
          tripTab={tripTab}
          setTripTab={setTripTab}
          tripMode={tripMode}
          setTripMode={setTripMode}
          tripType={tripType}
          setTripType={setTripType}
          cabinTab={cabinTab}
          setCabinTab={setCabinTab}
          passengers={passengers}
          setPassengers={setPassengers}
          from={from}
          setFrom={setFrom}
          to={to}
          setTo={setTo}
          counts={counts}
          setCounts={setCounts}
          swap={swap}
          result={result}
          setResult={setResult}
          //accomadation
          accomCountry={accomCountry}
          setAccomCountry={setAccomCountry}
          accomType={accomType}
          setAccomType={setAccomType}
          accomRooms={accomRooms}
          setAccomRooms={setAccomRooms}
          accomNights={accomNights}
          setAccomNights={setAccomNights}
          accomResult={accomResult}
          setAccomResult={setAccomResult}
          //cars
          carVehicleType={carVehicleType}
          setCarVehicleType={setCarVehicleType}
          carDistance={carDistance}
          setCarDistance={setCarDistance}
          carFuelType={carFuelType}
          setCarFuelType={setCarFuelType}
          carFuelConsumed={carFuelConsumed}
          setCarFuelConsumed={setCarFuelConsumed}
          carResult={carResult}
          setCarResult={setCarResult}
          //boats
          boatType={boatType}
          setBoatType={setBoatType}
          boatDays={boatDays}
          setBoatDays={setBoatDays}
          boatPeople={boatPeople}
          setBoatPeople={setBoatPeople}
          boatResult={boatResult}
          setBoatResult={setBoatResult}
          //trains
          trainTripMode={trainTripMode}
          setTrainTripMode={setTrainTripMode}
          trainTripType={trainTripType}
          setTrainTripType={setTrainTripType}
          trainPassengers={trainPassengers}
          setTrainPassengers={setTrainPassengers}
          trainClass={trainClass}
          setTrainClass={setTrainClass}
          trainFrom={trainFrom}
          setTrainFrom={setTrainFrom}
          trainTo={trainTo}
          setTrainTo={setTrainTo}
          trainCounts={trainCounts}
          setTrainCounts={setTrainCounts}
          trainSwap={trainSwap}
          trainResult={trainResult}
          setTrainResult={setTrainResult}
          selectedTrip={selectedTrip}
          setSelectedTrip={setSelectedTrip}
          tripName={tripName}
          setTripName={setTripName}
        />
      </Box>

      {/* ─── Right: Result Card ──────────────────────────────── */}
      <Box
        flex="1"
        bg="white"
        p={6}
        borderRadius="lg"
        boxShadow="md"
        minW={{ base: "full", md: "400px" }}
      >
        {/* Header */}
        <Heading fontSize="lg" mb={4}>
          <Translate text="Your Carbon Footprint" />
        </Heading>

        {/* Cloud graphic */}
        <Box
          borderRadius="md"
          display="inline-flex"
          flexDir="column"
          alignItems="center"
          w="full"
          mb={8}
        >
          <Image
            src={CloudIcon}
            objectFit="contain"
            alt="Carbon cloud footprint"
            h="100px"
            mb={1}
          />
        </Box>

        {/* Cause selection */}
        <Box mb={4}>
          <Text fontSize="sm" color="gray.600" mb={1}>
            <Translate text="Cause (required)" />
          </Text>
          <Select
            value={cause || ""}
            placeholder={Translate({ text: "Select a cause", as: "string" })}
            onChange={(e) => setCause(e.target.value)}
            bg="white"
            borderColor="#ccc"
            _focus={{ borderColor: "#286328" }}
          >
            <option value="The Ocean Cleanup">The Ocean Cleanup</option>
            <option value="Planet Impact Fund">Planet Impact Fund</option>
          </Select>
        </Box>

        {/* Emission Footprint row (cumulative) */}
        {/* <Heading fontSize="sm" mb={2}>
          <Translate text="Total Footprint" />
        </Heading>
        <Flex justify="space-between" align="center" mb={1}>
          <Text fontSize="sm" color="gray.700" mb={0}>
            <Translate text="All Travel Types" />
          </Text>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" mb={0}>
              {Number(convertedCost).toFixed(2)} {currency}
            </Text>
          </Flex>
        </Flex> */}

        {/* <Divider my={2} /> */}

        {/* Totals */}
        {/* <Flex justify="space-between" mb={2}>
          <Text fontSize="sm" color="gray.600" mb={0}>
            <Translate text="Total Amount" />
          </Text>
          <Text fontSize="sm" mb={0}>
            {Number(convertedCost).toFixed(2)} {currency}
          </Text>
        </Flex> */}
        {/* Breakdown by Category */}
        {emissionBreakdown.flight ||
        emissionBreakdown.accommodation ||
        emissionBreakdown.car ||
        emissionBreakdown.boat ||
        emissionBreakdown.train ? (
          <>
            {/* <Divider my={2} />
            <Heading fontSize="sm" mb={2}>
              <Translate text="By Category" />
            </Heading> */}
            {emissionBreakdown.flight > 0 && (
              <Flex justify="space-between" align="center" mb={1}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={
                    excludedCategories.has("flight") ? "gray.400" : "gray.700"
                  }
                  mb={0}
                  textDecoration={
                    excludedCategories.has("flight") ? "line-through" : "none"
                  }
                >
                  <Translate text="Flights" />
                </Text>
              </Flex>
            )}
            {emissionBreakdown.flight > 0 &&
              !excludedCategories.has("flight") && (
                <Grid
                  templateColumns={
                    isFlightMulti ? "2fr 1fr auto" : "2fr 1fr 1fr auto"
                  }
                  alignItems="center"
                  gap={3}
                  mb={2}
                  opacity={customCost !== "" ? 0.5 : 1}
                >
                  {/* Col 1: Route or multi-trip summary */}
                  {isFlightMulti ? (
                    <HStack spacing={3} align="center" flexWrap="nowrap">
                      <Text
                        whiteSpace="nowrap"
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        Short: {counts.short}
                      </Text>
                      <Text
                        whiteSpace="nowrap"
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        Medium: {counts.medium}
                      </Text>
                      <Text
                        whiteSpace="nowrap"
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        Long: {counts.long}
                      </Text>
                    </HStack>
                  ) : (
                    <Flex align="center" minW={0} gap={2}>
                      <Text
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        {from || "—"}
                      </Text>
                      <Icon
                        as={FaPlaneDeparture}
                        boxSize={3}
                        color={customCost !== "" ? "gray.400" : "gray.500"}
                      />
                      <Text
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        {to || "—"}
                      </Text>
                    </Flex>
                  )}
                  {/* Col 2: Passengers (hidden for multi-trip) */}
                  {!isFlightMulti && (
                    <HStack spacing={1} justifySelf="center">
                      <Icon
                        as={FaUser}
                        boxSize={3}
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                      />
                      <Text
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        {passengers || 1}
                      </Text>
                    </HStack>
                  )}
                  {/* Col 3: Class */}
                  <Text
                    fontSize="xs"
                    color={customCost !== "" ? "gray.400" : "gray.600"}
                    mb={0}
                    textAlign="center"
                    justifySelf="center"
                  >
                    {capitalize(cabinTab)}
                  </Text>
                  {/* Col 4: Amount + delete */}
                  <Flex align="center" gap={2} justifySelf="end">
                    <Text
                      fontSize="xs"
                      mb={0}
                      color={customCost !== "" ? "gray.400" : "inherit"}
                    >
                      {Number(categoryAmounts.flight).toFixed(2)} {currency}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      p={1}
                      minW="auto"
                      onClick={() => toggleCategoryExclusion("flight")}
                      color={
                        excludedCategories.has("flight")
                          ? "green.500"
                          : "red.500"
                      }
                      isDisabled={customCost !== ""}
                      opacity={customCost !== "" ? 0.5 : 1}
                    >
                      <DeleteIcon boxSize={3} />
                    </Button>
                  </Flex>
                </Grid>
              )}
            {emissionBreakdown.accommodation > 0 && (
              <Flex justify="space-between" align="center" mb={1}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={
                    excludedCategories.has("accommodation")
                      ? "gray.400"
                      : "gray.700"
                  }
                  mb={0}
                  textDecoration={
                    excludedCategories.has("accommodation")
                      ? "line-through"
                      : "none"
                  }
                >
                  <Translate text="Accommodation" />
                </Text>
              </Flex>
            )}
            {emissionBreakdown.accommodation > 0 &&
              !excludedCategories.has("accommodation") && (
                <Grid
                  templateColumns="2fr 1fr 1fr auto"
                  alignItems="center"
                  gap={3}
                  mb={2}
                  opacity={customCost !== "" ? 0.5 : 1}
                >
                  {/* Col 1: Room type */}
                  <Text
                    fontSize="xs"
                    color={customCost !== "" ? "gray.400" : "gray.600"}
                    mb={0}
                  >
                    {capitalize(accomType)} room {accomRooms}x
                  </Text>
                  {/* Col 2: Nights */}
                  <HStack spacing={1} justifySelf="center">
                    <Icon
                      as={FaMoon}
                      boxSize={3}
                      color={customCost !== "" ? "gray.400" : "gray.600"}
                    />
                    <Text
                      fontSize="xs"
                      color={customCost !== "" ? "gray.400" : "gray.600"}
                      mb={0}
                    >
                      {accomNights || 1}
                    </Text>
                  </HStack>
                  {/* Col 3: Service */}
                  <Text
                    fontSize="xs"
                    color={customCost !== "" ? "gray.400" : "gray.600"}
                    mb={0}
                    textAlign="center"
                    justifySelf="center"
                  >
                    Breakfast
                  </Text>
                  {/* Col 4: Amount + delete */}
                  <Flex align="center" gap={2} justifySelf="end">
                    <Text
                      fontSize="xs"
                      mb={0}
                      color={customCost !== "" ? "gray.400" : "inherit"}
                    >
                      {Number(categoryAmounts.accommodation).toFixed(2)}{" "}
                      {currency}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      p={1}
                      minW="auto"
                      onClick={() => toggleCategoryExclusion("accommodation")}
                      color={
                        excludedCategories.has("accommodation")
                          ? "green.500"
                          : "red.500"
                      }
                      isDisabled={customCost !== ""}
                      opacity={customCost !== "" ? 0.5 : 1}
                    >
                      <DeleteIcon boxSize={3} />
                    </Button>
                  </Flex>
                </Grid>
              )}
            {emissionBreakdown.car > 0 && (
              <Flex justify="space-between" align="center" mb={1}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={
                    excludedCategories.has("car") ? "gray.400" : "gray.700"
                  }
                  mb={0}
                  textDecoration={
                    excludedCategories.has("car") ? "line-through" : "none"
                  }
                >
                  <Translate text="Car" />
                </Text>
              </Flex>
            )}
            {emissionBreakdown.car > 0 && !excludedCategories.has("car") && (
              <Grid
                templateColumns="2fr 1fr 1fr auto"
                alignItems="center"
                gap={3}
                mb={2}
                opacity={customCost !== "" ? 0.5 : 1}
              >
                {/* Col 1: Vehicle type */}
                <Text
                  fontSize="xs"
                  color={customCost !== "" ? "gray.400" : "gray.600"}
                  mb={0}
                >
                  {capitalize(carVehicleType)}
                </Text>
                {/* Col 2: Distance */}
                <Text
                  fontSize="xs"
                  color={customCost !== "" ? "gray.400" : "gray.600"}
                  mb={0}
                  textAlign="center"
                  justifySelf="center"
                >
                  {carDistance || 0} km
                </Text>
                {/* Col 3: Fuel type */}
                <Text
                  fontSize="xs"
                  color={customCost !== "" ? "gray.400" : "gray.600"}
                  mb={0}
                  textAlign="center"
                  justifySelf="center"
                >
                  {capitalize(carFuelType)}
                </Text>
                {/* Col 4: Amount + delete */}
                <Flex align="center" gap={2} justifySelf="end">
                  <Text
                    fontSize="xs"
                    mb={0}
                    color={customCost !== "" ? "gray.400" : "inherit"}
                  >
                    {Number(categoryAmounts.car).toFixed(2)} {currency}
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    p={1}
                    minW="auto"
                    onClick={() => toggleCategoryExclusion("car")}
                    color={
                      excludedCategories.has("car") ? "green.500" : "red.500"
                    }
                    isDisabled={customCost !== ""}
                    opacity={customCost !== "" ? 0.5 : 1}
                  >
                    <DeleteIcon boxSize={3} />
                  </Button>
                </Flex>
              </Grid>
            )}
            {emissionBreakdown.boat > 0 && (
              <Flex justify="space-between" align="center" mb={1}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={
                    excludedCategories.has("boat") ? "gray.400" : "gray.700"
                  }
                  mb={0}
                  textDecoration={
                    excludedCategories.has("boat") ? "line-through" : "none"
                  }
                >
                  <Translate text="Boat" />
                </Text>
              </Flex>
            )}
            {emissionBreakdown.boat > 0 && !excludedCategories.has("boat") && (
              <Grid
                templateColumns="2fr 1fr 1fr auto"
                alignItems="center"
                gap={3}
                mb={2}
                opacity={customCost !== "" ? 0.5 : 1}
              >
                {/* Col 1: Boat type */}
                <Text
                  fontSize="xs"
                  color={customCost !== "" ? "gray.400" : "gray.600"}
                  mb={0}
                >
                  {capitalize(boatType)}
                </Text>
                {/* Col 2: Days */}
                <Text
                  fontSize="xs"
                  color={customCost !== "" ? "gray.400" : "gray.600"}
                  mb={0}
                  textAlign="center"
                  justifySelf="center"
                >
                  {boatDays} days
                </Text>
                {/* Col 3: People */}
                <HStack spacing={1} justifySelf="center">
                  <Icon
                    as={FaUser}
                    boxSize={3}
                    color={customCost !== "" ? "gray.400" : "gray.600"}
                  />
                  <Text
                    fontSize="xs"
                    color={customCost !== "" ? "gray.400" : "gray.600"}
                    mb={0}
                  >
                    {boatPeople}
                  </Text>
                </HStack>
                {/* Col 4: Amount + delete */}
                <Flex align="center" gap={2} justifySelf="end">
                  <Text
                    fontSize="xs"
                    mb={0}
                    color={customCost !== "" ? "gray.400" : "inherit"}
                  >
                    {Number(categoryAmounts.boat).toFixed(2)} {currency}
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    p={1}
                    minW="auto"
                    onClick={() => toggleCategoryExclusion("boat")}
                    color={
                      excludedCategories.has("boat") ? "green.500" : "red.500"
                    }
                    isDisabled={customCost !== ""}
                    opacity={customCost !== "" ? 0.5 : 1}
                  >
                    <DeleteIcon boxSize={3} />
                  </Button>
                </Flex>
              </Grid>
            )}
            {emissionBreakdown.train > 0 && (
              <Flex justify="space-between" align="center" mb={1}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={
                    excludedCategories.has("train") ? "gray.400" : "gray.700"
                  }
                  mb={0}
                  textDecoration={
                    excludedCategories.has("train") ? "line-through" : "none"
                  }
                >
                  <Translate text="Train" />
                </Text>
              </Flex>
            )}
            {emissionBreakdown.train > 0 &&
              !excludedCategories.has("train") && (
                <Grid
                  templateColumns={isTrainMulti ? "2fr 1fr auto" : "2fr 1fr 1fr auto"}
                  alignItems="center"
                  gap={3}
                  mb={2}
                  opacity={customCost !== "" ? 0.5 : 1}
                >
                  {/* Col 1: Route or multi-trip summary */}
                  {isTrainMulti ? (
                    <HStack spacing={3} align="center" flexWrap="nowrap">
                      <Text whiteSpace="nowrap" fontSize="xs" color={customCost !== "" ? "gray.400" : "gray.600"} mb={0}>Short: {trainCounts.short}</Text>
                      <Text whiteSpace="nowrap" fontSize="xs" color={customCost !== "" ? "gray.400" : "gray.600"} mb={0}>Medium: {trainCounts.medium}</Text>
                      <Text whiteSpace="nowrap" fontSize="xs" color={customCost !== "" ? "gray.400" : "gray.600"} mb={0}>Long: {trainCounts.long}</Text>
                    </HStack>
                  ) : (
                    <Flex align="center" minW={0} gap={2}>
                      <Text
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        {trainFrom || "—"}
                      </Text>
                      <Icon
                        as={FaPlaneDeparture}
                        boxSize={3}
                        color={customCost !== "" ? "gray.400" : "gray.500"}
                      />
                      <Text
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        {trainTo || "—"}
                      </Text>
                    </Flex>
                  )}
                  {/* Col 2: Passengers (hidden for multi-trip) */}
                  {!isTrainMulti && (
                    <HStack spacing={1} justifySelf="center">
                      <Icon
                        as={FaUser}
                        boxSize={3}
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                      />
                      <Text
                        fontSize="xs"
                        color={customCost !== "" ? "gray.400" : "gray.600"}
                        mb={0}
                      >
                        {trainPassengers}
                      </Text>
                    </HStack>
                  )}
                  {/* Col 3: Class */}
                  <Text
                    fontSize="xs"
                    color={customCost !== "" ? "gray.400" : "gray.600"}
                    mb={0}
                    textAlign="center"
                    justifySelf="center"
                  >
                    {capitalize(trainClass)}
                  </Text>
                  {/* Col 4: Amount + delete */}
                  <Flex align="center" gap={2} justifySelf="end">
                    <Text
                      fontSize="xs"
                      mb={0}
                      color={customCost !== "" ? "gray.400" : "inherit"}
                    >
                      {Number(categoryAmounts.train).toFixed(2)} {currency}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      p={1}
                      minW="auto"
                      onClick={() => toggleCategoryExclusion("train")}
                      color={
                        excludedCategories.has("train")
                          ? "green.500"
                          : "red.500"
                      }
                      isDisabled={customCost !== ""}
                      opacity={customCost !== "" ? 0.5 : 1}
                    >
                      <DeleteIcon boxSize={3} />
                    </Button>
                  </Flex>
                </Grid>
              )}
          </>
        ) : null}
        <Divider my={2} />
        <Flex justify="space-between" mb={4} align="center">
          <Text fontSize="sm" color="black" mb={0}>
            <Translate text="Total Footprint" />
          </Text>
          <HStack spacing={1}>
            {isEditingCost ? (
              <>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tempCost}
                  onChange={(e) => {
                    // Only allow up to 2 decimals
                    const val = e.target.value;
                    if (/^\d*(\.\d{0,2})?$/.test(val)) setTempCost(val);
                  }}
                  style={{
                    width: "100px",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                />
                <Button
                  size="xs"
                  ml={1}
                  onClick={() => {
                    setCustomCost(Number(tempCost).toFixed(2));
                    setIsEditingCost(false);
                  }}
                  p={1}
                  minW={"auto"}
                >
                  <CheckIcon color="#286328" />
                </Button>
                <Button
                  size="xs"
                  ml={1}
                  onClick={() => {
                    setIsEditingCost(false);
                    setTempCost(
                      customCost !== "" ? customCost : convertedCost.toFixed(2)
                    );
                  }}
                  p={1}
                  minW={"auto"}
                >
                  <CloseIcon />
                </Button>
              </>
            ) : (
              <>
                <Text fontSize="sm" fontWeight="bold" mb={0}>
                  {Number(displayCost).toFixed(2)} {currency}
                </Text>
                <Button
                  size="xs"
                  ml={2}
                  onClick={() => {
                    setIsEditingCost(true);
                    setTempCost(
                      customCost !== "" ? customCost : convertedCost.toFixed(2)
                    );
                  }}
                  variant="ghost"
                  p={1}
                  minW={"auto"}
                >
                  <EditIcon />
                </Button>
              </>
            )}
          </HStack>
        </Flex>

        {/* Error message */}
        {error && (
          <Text color="red.500" mb={2} fontSize="sm">
            {error}
          </Text>
        )}
        {/* CTA */}
        <Button
          w="full"
          bg={GOLD}
          color="black"
          _hover={{ bg: GOLD }}
          borderRadius="lg"
          fontWeight="bold"
          py={6}
          isLoading={loading}
          onClick={handleOffsetNow}
          isDisabled={!totalEmissionsKg || !cause || loading}
        >
          <Translate text="Offset Now" />
        </Button>
      </Box>
    </Flex>
  );
};

export default OffsetCalculator;
